import type { CustomAnalyticsSemanticQuery } from "@redotech/redo-analytics-custom-analytics/semantic-queries/v1/analytics-query/analytics-query-types";
import * as z from "zod";
import {
  CUSTOM_ANALYTICS_REPORT_COLUMN_LABEL_MAX_LENGTH,
  type CustomAnalyticsReportColumnPresentation,
} from "./custom-analytics-report";

export const CustomAnalyticsReportColumnLabelUpdateSchema = z.object({
  sourceName: z.string().trim().min(1),
  label: z
    .string()
    .transform((value) => value.replace(/\s+/g, " ").trim())
    .pipe(
      z.string().min(1).max(CUSTOM_ANALYTICS_REPORT_COLUMN_LABEL_MAX_LENGTH),
    ),
});

export type CustomAnalyticsReportColumnLabelUpdate = z.infer<
  typeof CustomAnalyticsReportColumnLabelUpdateSchema
>;

export const CustomAnalyticsReportColumnLabelUpdatesSchema = z
  .array(CustomAnalyticsReportColumnLabelUpdateSchema)
  .max(45);

export class InvalidCustomAnalyticsReportColumnLabelError extends Error {}

export function replaceCustomAnalyticsReportDefinitionColumnLabels({
  columnLabels,
  columnPresentation,
  semanticQuery,
}: {
  columnLabels: readonly CustomAnalyticsReportColumnLabelUpdate[];
  columnPresentation: readonly CustomAnalyticsReportColumnPresentation[] | null;
  semanticQuery: CustomAnalyticsSemanticQuery;
}): CustomAnalyticsReportColumnPresentation[] {
  const parsedColumnLabels =
    CustomAnalyticsReportColumnLabelUpdatesSchema.safeParse(columnLabels);
  if (
    !columnPresentation ||
    columnPresentation.length !== semanticQuery.columns.length ||
    !parsedColumnLabels.success
  ) {
    throw new InvalidCustomAnalyticsReportColumnLabelError();
  }

  const presentationBySourceName = new Map(
    columnPresentation.map((presentation) => [
      presentation.sourceName,
      presentation,
    ]),
  );
  if (presentationBySourceName.size !== columnPresentation.length) {
    throw new InvalidCustomAnalyticsReportColumnLabelError();
  }

  const labelsBySourceName = new Map<string, string>();
  for (const { label, sourceName } of parsedColumnLabels.data) {
    if (
      labelsBySourceName.has(sourceName) ||
      !presentationBySourceName.has(sourceName)
    ) {
      throw new InvalidCustomAnalyticsReportColumnLabelError();
    }
    labelsBySourceName.set(sourceName, label);
  }

  return columnPresentation.map((presentation) => {
    const label = labelsBySourceName.get(presentation.sourceName);
    return label === undefined
      ? presentation
      : { ...presentation, label, labelManuallyEdited: true };
  });
}

export function preserveManuallyEditedCustomAnalyticsReportColumnLabels({
  currentColumnPresentation,
  nextColumnPresentation,
}: {
  currentColumnPresentation:
    | readonly CustomAnalyticsReportColumnPresentation[]
    | null;
  nextColumnPresentation: readonly CustomAnalyticsReportColumnPresentation[];
}): CustomAnalyticsReportColumnPresentation[] {
  const currentPresentationBySourceName = new Map(
    currentColumnPresentation?.map((presentation) => [
      presentation.sourceName,
      presentation,
    ]),
  );

  return nextColumnPresentation.map((presentation) => {
    const currentPresentation = currentPresentationBySourceName.get(
      presentation.sourceName,
    );
    if (
      currentPresentation?.labelManuallyEdited !== true ||
      currentPresentation.label === null
    ) {
      return presentation;
    }
    return {
      ...presentation,
      label: currentPresentation.label,
      labelManuallyEdited: true,
    };
  });
}
